import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, setBlockTracking as _setBlockTracking, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["onPointerdownPassive"]
const _hoisted_2 = { class: "flag top-right" }
const _hoisted_3 = {
  key: 1,
  class: "video"
}
const _hoisted_4 = {
  key: 0,
  class: "time"
}
const _hoisted_5 = {
  key: 3,
  class: "pano"
}
const _hoisted_6 = { class: "flag bottom-right" }
const _hoisted_7 = { class: "flag bottom-left" }
const _hoisted_8 = {
  key: 0,
  class: "shared-by"
}
const _hoisted_9 = {
  key: 1,
  class: "basename"
}
const _hoisted_10 = ["src"]
const _hoisted_11 = {
  key: 2,
  class: "overlay overlay-bottom top-left fill-block"
}

import { computed, onBeforeUnmount, onMounted, reactive, ref, useTemplateRef, watch } from 'vue';

import { RenewingTimeout } from '@services/utils/renewing-timeout';
import { constants } from '@services/constants';
import { config } from '@services/user-config';
import { t } from '@services/l10n';
import { useRouteIs } from '@services/route-is';
import * as utils from '@services/utils/common';

import LivePhotoIcon from '@components/icons/LivePhoto.vue';
import CheckCircleIcon from 'vue-material-design-icons/CheckCircle.vue';
import StarIcon from 'vue-material-design-icons/Star.vue';
import VideoIcon from 'vue-material-design-icons/PlayCircleOutline.vue';
import PanoramaSphereIcon from 'vue-material-design-icons/PanoramaSphereOutline.vue';
import LocalIcon from 'vue-material-design-icons/CloudOff.vue';
import RawIcon from 'vue-material-design-icons/Raw.vue';

import type { IDay, IPhoto } from '@typings';
import XImg from '@components/frame/XImg.vue';

import errorsvg from '@assets/error.svg';


export default /*@__PURE__*/_defineComponent({
  ...{
  name: 'Photo',
},
  __name: 'Photo',
  props: {
    data: {},
    day: {}
  },
  emits: ["select", "pointerdown", "touchstart", "touchmove", "touchend"],
  setup(__props: any, { emit: __emit }) {



const props = __props;

const emit = __emit;
const root = useTemplateRef<HTMLDivElement>('root');
const routeIs = useRouteIs();
const ximg = useTemplateRef<InstanceType<typeof XImg> & { $el: HTMLImageElement }>('ximg');
const video = useTemplateRef<HTMLVideoElement>('video');

const liveState = reactive({
  playing: false,
  waiting: false,
  requested: false,
});
const livePlayTimer = new RenewingTimeout();
const faceSrc = ref<string | null>(null);

watch(
  () => props.data,
  (newData: IPhoto, oldData: IPhoto) => {
    // Copy flags relevant to this component
    if (oldData && newData) {
      newData.flag |= oldData.flag & (constants.FLAG_SELECTED | constants.FLAG_LOAD_FAIL);
    }
  },
);

onMounted(() => {
  faceSrc.value = null;

  // Expose the IPhoto on the DOM element directly
  Object.defineProperty(root.value, '__photo', { get: () => props.data });

  // Setup video hooks
  if (video.value) {
    utils.setupLivePhotoHooks(video.value, liveState);
  }
});

/** Clear timers */
onBeforeUnmount(() => {
  livePlayTimer.clear();

  // Clean up blob url if face rect was created
  if (faceSrc.value) {
    URL.revokeObjectURL(faceSrc.value);
  }
});

const videoDuration = computed((): string | null => {
  if (props.data.video_duration) {
    return utils.getDurationStr(props.data.video_duration);
  }
  return null;
});

const videoUrl = computed((): string | null => {
  if (props.data.liveid) {
    return utils.getLivePhotoVideoUrl(props.data, true);
  }
  return null;
});

const src = computed((): string | null => {
  props.data.etag; // dependency

  if (props.data.flag & constants.FLAG_PLACEHOLDER) {
    return null;
  } else if (props.data.flag & constants.FLAG_LOAD_FAIL) {
    return errorsvg;
  } else if (faceSrc.value) {
    return faceSrc.value;
  } else {
    return url();
  }
});

const isRaw = computed((): boolean => {
  return !!props.data.stackraw || props.data.mimetype === constants.MIME_RAW;
});

const showOwnerName = computed((): boolean => {
  if (routeIs.Base && !config.show_owner_name_timeline) {
    return false;
  }
  return true;
});

const showBasename = computed((): boolean => {
  return !!config.show_photo_basename && !!props.data.basename;
});

const sharedBy = computed((): string | null => {
  if (props.data.shared_by == '[unknown]') {
    return t('memories', 'Shared');
  } else if (props.data.shared_by) {
    return props.data.shared_by;
  }
  return null;
});

const hasBottomText = computed((): boolean => {
  return (showOwnerName.value && !!sharedBy.value) || showBasename.value;
});

/** Get url of the photo */
function url() {
  let base: 256 | 512 = 256;

  // Check if displayed size is larger than the image
  if (props.data.dispH! > base * 0.9 && props.data.dispW! > base * 0.9) {
    // Get a bigger image
    // 1. No trickery here, just get one size bigger. This is to
    //    ensure that the images can be cached even after reflow.
    // 2. Nextcloud only allows 4**x sized images, so technically
    //    this ends up being equivalent to 1024x1024.
    base = 512;
  }

  return utils.getPreviewUrl({
    photo: props.data,
    msize: base,
  });
}

/** Set src with overlay face rect */
async function addFaceRect() {
  if (!props.data.facerect || faceSrc.value) return;

  const img = ximg.value?.$el;
  if (!img) return;

  // This is a hack to check if img is actually loaded.
  //   XImg loads an empty image, which may sometimes show up here
  //   If the size is less than 5px it is probably this dummy image
  //   Either way, the user cannot see anything if the image is this small
  //   so there's no point in trying to draw the face rect
  if (!img || img.naturalWidth < 5) return;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return; // failed to create canvas

  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  context.drawImage(img, 0, 0);
  context.strokeStyle = '#00ff00';
  context.lineWidth = 2;
  context.strokeRect(
    props.data.facerect.x * img.naturalWidth,
    props.data.facerect.y * img.naturalHeight,
    props.data.facerect.w * img.naturalWidth,
    props.data.facerect.h * img.naturalHeight,
  );

  canvas.toBlob(
    (blob) => {
      if (!blob) return;
      faceSrc.value = URL.createObjectURL(blob);
    },
    'image/jpeg',
    0.95,
  );
}

/** Post load tasks */
function load() {
  addFaceRect();
}

/** Error in loading image */
function error(e: Error) {
  props.data.flag |= constants.FLAG_LOAD_FAIL;
}

function contextmenu(e: Event) {
  e.preventDefault();
  e.stopPropagation();
}

/** Start preview video */
function playVideo() {
  if (props.data.flag & constants.FLAG_SELECTED) return;
  liveState.waiting = true;

  // Quickly moving over the icon causes unnecessary
  // transcoding requests which are expensive
  livePlayTimer.set(
    async () => {
      if (!video.value || props.data.flag & constants.FLAG_SELECTED) return;

      try {
        liveState.requested = true;
        video.value.currentTime = 0;
        video.value.loop = true;
        await video.value.play();
      } catch (e) {
        // ignore, pause was probably called too soon
      } finally {
        liveState.waiting = false;
      }
    },
    liveState.requested ? 0 : 300, // delay only the first play
  );
}

/** Stop preview video */
function stopVideo() {
  video.value?.pause();
  livePlayTimer.clear();
  liveState.waiting = false;
}

/** Start/stop preview video for touchscreens */
function touchVideo() {
  if (liveState.playing) stopVideo();
  else playVideo();
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "root",
    ref: root,
    class: "p-outer-super"
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["p-outer fill-block", {
        selected: __props.data.flag & _unref(constants).FLAG_SELECTED,
        placeholder: __props.data.flag & _unref(constants).FLAG_PLACEHOLDER,
        leaving: __props.data.flag & _unref(constants).FLAG_LEAVING,
        error: __props.data.flag & _unref(constants).FLAG_LOAD_FAIL,
        [`p-outer--${__props.data.key}`]: true,
      }])
    }, [
      _cache[0] || (
        _setBlockTracking(-1, true),
        (_cache[0] = (!(__props.data.flag & _unref(constants).FLAG_PLACEHOLDER))
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "select",
              onPointerdownPassive: ($event: any) => (emit('select', $event))
            }, [
              _createVNode(CheckCircleIcon, { size: 18 })
            ], 40, _hoisted_1))
          : _createCommentVNode("", true)).cacheIndex = 0,
        _setBlockTracking(1),
        _cache[0]
      ),
      _createElementVNode("div", _hoisted_2, [
        (isRaw.value)
          ? (_openBlock(), _createBlock(RawIcon, {
              key: 0,
              class: "raw",
              size: 28
            }))
          : _createCommentVNode("", true),
        (__props.data.flag & _unref(constants).FLAG_IS_VIDEO)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              (__props.data.video_duration)
                ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(videoDuration.value), 1))
                : _createCommentVNode("", true),
              _createVNode(VideoIcon, { size: 22 })
            ]))
          : (__props.data.liveid)
            ? (_openBlock(), _createElementBlock("div", {
                key: 2,
                class: "livephoto",
                onMouseenterPassive: playVideo,
                onMouseleavePassive: stopVideo,
                onTouchstartPassive: touchVideo
              }, [
                _createVNode(LivePhotoIcon, {
                  size: "22px",
                  spin: liveState.waiting,
                  playing: liveState.playing
                }, null, 8, ["spin", "playing"])
              ], 32))
            : (__props.data.pano === 2)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createVNode(PanoramaSphereIcon, { size: 22 })
                ]))
              : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_6, [
        (__props.data.flag & _unref(constants).FLAG_IS_FAVORITE)
          ? (_openBlock(), _createBlock(StarIcon, {
              key: 0,
              size: 22
            }))
          : _createCommentVNode("", true),
        (__props.data.flag & _unref(constants).FLAG_IS_LOCAL)
          ? (_openBlock(), _createBlock(LocalIcon, {
              key: 1,
              size: 22
            }))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_7, [
        (showOwnerName.value && sharedBy.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(sharedBy.value), 1))
          : _createCommentVNode("", true),
        (showBasename.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(__props.data.basename), 1))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", {
        class: _normalizeClass(["img-outer fill-block", { 'memories-livephoto': __props.data.liveid }]),
        onContextmenu: contextmenu,
        onPointerdownPassive: _cache[1] || (_cache[1] = ($event: any) => (emit('pointerdown', $event))),
        onTouchstartPassive: _cache[2] || (_cache[2] = ($event: any) => (emit('touchstart', $event))),
        onTouchmove: _cache[3] || (_cache[3] = ($event: any) => (emit('touchmove', $event))),
        onTouchendPassive: _cache[4] || (_cache[4] = ($event: any) => (emit('touchend', $event))),
        onTouchcancelPassive: _cache[5] || (_cache[5] = ($event: any) => (emit('touchend', $event)))
      }, [
        (src.value)
          ? (_openBlock(), _createBlock(XImg, {
              ref_key: "ximg",
              ref: ximg,
              draggable: "false",
              class: _normalizeClass(["ximg fill-block no-user-select", [`memories-thumb-${__props.data.key}`]]),
              src: src.value,
              key: __props.data.fileid,
              onLoad: load,
              onError: error
            }, null, 8, ["class", "src"]))
          : _createCommentVNode("", true),
        (videoUrl.value)
          ? (_openBlock(), _createElementBlock("video", {
              key: 1,
              ref_key: "video",
              ref: video,
              src: videoUrl.value,
              preload: "none",
              muted: "",
              playsinline: "",
              disableRemotePlayback: ""
            }, null, 8, _hoisted_10))
          : _createCommentVNode("", true),
        _cache[6] || (_cache[6] = _createElementVNode("div", { class: "overlay overlay-top top-left fill-block" }, null, -1)),
        (hasBottomText.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_11))
          : _createCommentVNode("", true)
      ], 34)
    ], 2)
  ], 512))
}
}

})